import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { isAuthenticated } from '@/lib/auth/authSession'
import { resolvePostAuth } from '@/lib/auth/postAuthNavigate'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

export const Route = createFileRoute('/_AuthLayout/callback')({
  component: CallbackPage,
})

/**
 * Post-sign-in resolver. There is no OAuth redirect to complete any more
 * (Cognito's hosted UI is gone), but `/` still forwards signed-in users who
 * have no workspace in the store here, so this page looks up their user and
 * workspace and routes on to the app, /workspaces or /onboarding.
 *
 * Signed out (or an old bookmarked /callback?code=… link) → the sign-in page.
 */
function CallbackPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('auth')
  const [error, setError] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    let cancelled = false

    const handleAuth = async () => {
      if (!isAuthenticated()) {
        logger.logEvent(LogCategory.AUTH_CALLBACK, 'callback.not_authenticated_redirect_to_auth')
        void navigate({ to: '/auth', replace: true })
        return
      }

      try {
        const result = await resolvePostAuth()
        if (cancelled) return

        // Drop any stale query string (e.g. an old OAuth ?code=…).
        window.history.replaceState({}, document.title, window.location.pathname)

        void navigate({
          to: result.navigateTo,
          replace: true,
        })
      } catch (err) {
        if (cancelled) return
        let errorMessage = t('callback.errors.backend')
        let details = ''
        let status: number | undefined

        if (isAxiosError(err)) {
          status = err.response?.status
          details = status
            ? t('callback.errors.status', { status })
            : t('callback.errors.networkStatus')

          if (status === 401) {
            errorMessage = t('callback.errors.tokenInvalid')
          } else if (status === 403) {
            errorMessage = t('callback.errors.accessDenied')
          } else if (status && status >= 500) {
            errorMessage = t('callback.errors.server')
          } else if (!err.response) {
            errorMessage = t('callback.errors.unreachable')
          }
        }

        logger.logError(
          err as Error,
          LogCategory.AUTH_CALLBACK,
          { status, errorMessage }
        )

        setError(errorMessage)
        setErrorDetails(details)
      }
    }

    void handleAuth()
    return () => {
      cancelled = true
    }
  }, [retryCount, navigate, t])

  // Safety net: if the backend lookup hangs, surface the error UI so the user
  // can retry or sign out instead of staring at a spinner.
  useEffect(() => {
    if (error) return
    const timeoutId = window.setTimeout(() => {
      logger.logEvent(LogCategory.AUTH_CALLBACK, 'callback.timeout_safety_net')
      setError(t('callback.errors.timeout'))
      setErrorDetails(t('callback.errors.timeoutDetails'))
    }, 15_000)
    return () => window.clearTimeout(timeoutId)
  }, [error, retryCount, t])

  const handleRetry = () => {
    setError(null)
    setErrorDetails(null)
    setRetryCount(retryCount + 1)
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto space-y-6">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-lg">
          <div className="space-y-6">
            <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-destructive/10">
              <svg
                className="h-8 w-8 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <div className="space-y-2 text-center">
              <p className="text-xl font-semibold">{error}</p>
              {errorDetails && (
                <p className="text-sm text-muted-foreground">{errorDetails}</p>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={handleRetry}
                className="w-full"
              >
                {t('callback.retry')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setError(null)
                  setErrorDetails(null)
                  // Route through /logout so performLogout() revokes the
                  // session and clears the token and persisted stores.
                  // Navigating straight to /auth leaves the session intact,
                  // which bounces an access-denied user right back into this
                  // 403 via the route guards.
                  window.location.href = '/logout'
                }}
                className="w-full"
              >
                {t('callback.signOut')}
              </Button>
            </div>

            {retryCount > 2 && (
              <p className="text-xs text-muted-foreground text-center">
                {t('callback.support')}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="moly-spin flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
        <img
          src="/logo.png"
          alt={t('callback.logoAlt')}
          className="h-8 w-8 object-contain"
        />
      </div>
      <div className="space-y-1.5 text-center">
        <p className="text-base font-semibold">{t('callback.signingIn')}</p>
        <p className="text-sm text-muted-foreground">
          {t('callback.completing')}
        </p>
      </div>
    </div>
  )
}
